import { React, useRef } from "react";
import { memo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Slider from "react-slick";
import {
  getRecommandedProperty,
  getRecommendedPropertyWatchList,
  getCommercialProperties,
  getSignatureHomes,
  getNewToHubzu,
  getPropertiesNearYou,
  getSavedProperties,
  getAlerts,
} from "@/api/home";
import { sliderSettingHome, sliderSettingHomeAlerts } from "../data";
import PropertyCard from "./PropertyCard";
import HomeAlertCard from "./HomeAlertCard";
import SavedPropertyCard from "./SavedPropertyCard";
import HomeEmptyCard from "./HomeEmptyCard";
import { ShimmerPostList, ShimmerTitle } from "react-shimmer-effects";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";
import { MdHome, MdNotifications   } from "react-icons/md";
import { getEventCategory, trackButtonClick } from "@/utils/analytics";
import "../../srp/CardComponents/SRPCard.css";
import "./HomeProperties.css";

const TABS = [
  { key: 'alerts', label: 'Alerts', icon: MdNotifications },
  { key: 'saved-properties', label: 'Saved Properties', icon: MdHome },
  { key: 'properties-near-you', label: 'Properties Near You' },
  { key: 'new-to-hubzu', label: 'New to Hubzu' },
  { key: 'hubzu-signature-homes', label: 'Hubzu Signature Homes' },
  { key: 'commercial-properties', label: 'Commercial Properties' }
];

const HomeProperties = () => {
  const sliderRef = useRef(null);
  const settings = sliderSettingHome;
const settings1 = sliderSettingHomeAlerts
  const { savedSearch } = useSelector((state) => state.savedSearch);
  const userEmailId = useSelector((state) => state.user?.user?.userEmailId);
  const isUserLoggedIn = useSelector((state) => state.user?.isUserLoggedIn);
  const globalPopup = useSelector((state) => state.login);

  const [slideCount, setSlideCount] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(isUserLoggedIn ? TABS[2].key : TABS[2].key);
  const [isMobile, setIsMobile] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    const handleResize = () => {
      checkMobile();
      setWindowWidth(window.innerWidth);
    };

    checkMobile();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getProperties = async (tabType) => {
    try {
      setIsLoading(true);
      let properties = [];
      if (tabType === 'commercial-properties') {
        properties = await getCommercialProperties();
      } else if (tabType === 'hubzu-signature-homes') {
        properties = await getSignatureHomes();
      } else if (tabType === 'new-to-hubzu') {
        properties = await getNewToHubzu();
      } else if (tabType === 'properties-near-you') {
        properties = await getPropertiesNearYou();
      } else if (tabType === 'saved-properties') {
        properties = await getSavedProperties(userEmailId, 0, 10);
        const updatedProperties = properties.map((prop) => ({
          ...prop,
          onWatchList: "Y",
        }));
        setProperties(updatedProperties);
      } else if (tabType === 'alerts') {
        properties = await getAlerts();
      }
      if (!properties || properties?.length <= 0) {
        setProperties([]);
      }
      if (properties && tabType !== 'saved-properties') {
        const propertyIds = properties.map((p) => p?.propertyId).join(",");

        const watchListResponse = await getRecommendedPropertyWatchList([
          propertyIds,
        ]);

        const watchlistedIds = new Set(watchListResponse);

        // Map properties and update isOnWatchList flag
        const updatedProperties = properties.map((prop) => ({
          ...prop,
          onWatchList: watchlistedIds.has(prop.propertyId) ? "Y" : "N",
        }));

        setProperties(updatedProperties);
      }
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getProperties('properties-near-you');
    setActiveTab('properties-near-you');
  }, [userEmailId, globalPopup?.isUserSubscribed]);

  const handleTabClick = (tabType) => {
    setActiveTab(tabType);
    setCurrentSlide(0);
    getProperties(tabType);
  };

  const LoaderComponent = () => {
    return (
      <section id="feature-property" className="home-container pb30 pt0 mt20">
        {/* <div className="home-title">
          <ShimmerTitle line={1} gap={20} variant="primary" />
        </div> */}
        <ShimmerPostList postStyle="STYLE_FOUR" col={4} row={1} gap={20} />
      </section>
    );
  };

  const goToNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
      trackButtonClick({
        eventName: "recommended_slider_nav_click",
        eventCategory: getEventCategory(),
        eventLabel: "Section position: 4",
        eventParams: { direction: "right" },
      });
    }
  };

  const goToPrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
      trackButtonClick({
        eventName: "recommended_slider_nav_click",
        eventCategory: getEventCategory(),
        eventLabel: "Section position: 4",
        eventParams: { direction: "left" },
      });
    }
  };

  const customSettings = {
    ...settings,
    arrows: false, // This will override the arrows=true in the original settings
    slidesToShow:
      activeTab === "alerts" || activeTab === "saved-properties"
        ? 3
        : settings.slidesToShow,
    slidesToScroll:
      activeTab === "alerts" || activeTab === "saved-properties"
        ? 3
        : settings.slidesToScroll,
    infinite: activeTab === "alerts" ? false : settings.infinite,
    // handlers to track the current slide
    beforeChange: (current, next) => {
      setCurrentSlide(next);
    },
    afterChange: (current) => {
      setCurrentSlide(current);
      // Handle accessibility after slide change
      setTimeout(() => {
        const carouselContainer = document.querySelector('#feature-property');
        if (!carouselContainer) return;

        const hiddenSlides = carouselContainer.querySelectorAll('.slick-slide[aria-hidden="true"]');
        const visibleSlides = carouselContainer.querySelectorAll('.slick-slide:not([aria-hidden="true"])');

        hiddenSlides.forEach((slide) => {
          // Target ALL focusable elements including those with tabindex="0"
          const focusableElements = slide.querySelectorAll('button, a, input, select, textarea, [tabindex="0"], .cardTitle, .list-inline-item.head');
          focusableElements.forEach((el) => {
            el.setAttribute('tabindex', '-1');
            el.setAttribute('aria-hidden', 'true');
          });
        });

        visibleSlides.forEach((slide) => {
          const focusableElements = slide.querySelectorAll('button, a, input, select, textarea, [tabindex="-1"], .cardTitle, .list-inline-item.head');
          focusableElements.forEach((el) => {
            // Restore original tabindex for specific elements
            if (el.classList.contains('cardTitle') || el.classList.contains('head')) {
              el.setAttribute('tabindex', '0');
            } else {
              el.removeAttribute('tabindex');
            }
            el.removeAttribute('aria-hidden');
          });
        });
      }, 50);
    },
    onReInit: () => {
      if (sliderRef.current) {
        setSlideCount(properties.length);
      }
    },
    onInit: () => {
      if (properties.length) {
        setSlideCount(properties.length);
      }
    },
  };

    const customSettings1 = {
    ...settings1,
    arrows: false, // This will override the arrows=true in the original settings
    slidesToShow: activeTab === 'alerts' || activeTab === 'saved-properties' ? 3 :settings1.slidesToShow,
    slidesToScroll: activeTab === 'alerts' || activeTab === 'saved-properties' ? 3 : settings1.slidesToScroll,
    // handlers to track the current slide
    beforeChange: (current, next) => {
      setCurrentSlide(next);
    },
    afterChange: (current) => {
      setCurrentSlide(current);
      // Handle accessibility after slide change - same as customSettings
      setTimeout(() => {
        const carouselContainer = document.querySelector('#feature-property');
        if (!carouselContainer) return;

        const hiddenSlides = carouselContainer.querySelectorAll('.slick-slide[aria-hidden="true"]');
        const visibleSlides = carouselContainer.querySelectorAll('.slick-slide:not([aria-hidden="true"])');

        hiddenSlides.forEach((slide) => {
          const focusableElements = slide.querySelectorAll('button, a, input, select, textarea');
          focusableElements.forEach((el) => {
            el.setAttribute('tabindex', '-1');
            el.setAttribute('aria-hidden', 'true');
          });
        });

        visibleSlides.forEach((slide) => {
          const focusableElements = slide.querySelectorAll('button, a, input, select, textarea, [tabindex="-1"], .cardTitle, .list-inline-item.head');
          focusableElements.forEach((el) => {
            // Restore original tabindex for specific elements
            if (el.classList.contains('cardTitle') || el.classList.contains('head')) {
              el.setAttribute('tabindex', '0');
            } else {
              el.removeAttribute('tabindex');
            }
            el.removeAttribute('aria-hidden');
          });
        });
      }, 50);
    },
    onReInit: () => {
      if (sliderRef.current) {
        setSlideCount(properties.length);
      }
    },
    onInit: () => {
      if (properties.length) {
        setSlideCount(properties.length);
      }
    },
  };

  useEffect(() => {
    if (properties.length) {
      setSlideCount(properties.length);
    }
  }, [properties]);

  useEffect(() => {
    // Handle initial accessibility state and after any re-render
    const handleSlideAccessibility = () => {
      // Target only slides within your specific carousel
      const carouselContainer = document.querySelector('#feature-property');
      if (!carouselContainer) return;

      const hiddenSlides = carouselContainer.querySelectorAll('.slick-slide[aria-hidden="true"]');
      const visibleSlides = carouselContainer.querySelectorAll('.slick-slide:not([aria-hidden="true"])');

      // Disable focusable elements in hidden slides
      hiddenSlides.forEach((slide) => {
        const focusableElements = slide.querySelectorAll('button, a, input, select, textarea, [tabindex="0"], .cardTitle, .list-inline-item.head');
        focusableElements.forEach((el) => {
          el.setAttribute('tabindex', '-1');
          el.setAttribute('aria-hidden', 'true');
        });
      });

      // Enable focusable elements in visible slides
      visibleSlides.forEach((slide) => {
        const focusableElements = slide.querySelectorAll('button, a, input, select, textarea, [tabindex="-1"], .cardTitle, .list-inline-item.head');
        focusableElements.forEach((el) => {
          // Restore original tabindex for specific elements
          if (el.classList.contains('cardTitle') || el.classList.contains('head')) {
            el.setAttribute('tabindex', '0');
          } else {
            el.removeAttribute('tabindex');
          }
          el.removeAttribute('aria-hidden');
        });
      });
    };

    // Run immediately and after properties change
    const timer = setTimeout(handleSlideAccessibility, 100);
    return () => clearTimeout(timer);
  }, [properties, activeTab]);


  const renderTabs = () => {
    const tabsToShow = isUserLoggedIn ? TABS : TABS.slice(2);

    return (
      <div className="home-properties-tabs" role="tablist">
        {tabsToShow.map((tab) => (
          <button
            key={tab.key}
            id={`home-properties-tab-${tab.key}`}
            className={`home-properties-tab ${
              activeTab === tab.key ? "active" : ""
            }`}
            onClick={() => handleTabClick(tab.key)}
            role="tab"
            aria-selected={activeTab === tab.key}
            tabIndex={activeTab === tab.key ? "0" : "-1"}
          >
            {tab.icon && <tab.icon className="home-properties-tab-icon" />}
            <span className="home-properties-tab-text">{tab.label}</span>
          </button>
        ))}
      </div>
    );
  };

  return (
    <>
      <section id="feature-property" className=" pb30 pt50">
        <div className="home-title-container">
          {renderTabs()}
          {!isLoading && properties?.length > 0 && !isMobile && (
            <div className="custom-arrows">
              <button
                id="home-properties-prev-btn"
                onClick={goToPrev}
                aria-label="goToPrev"
                className={`custom-arrow prev ${
                  currentSlide === 0 ? "disabled" : ""
                }`}
                disabled={currentSlide === 0}
              >
                {currentSlide === 0 ? (
                  <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 25L9 17L17 9" stroke="#ADC4DA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 17L25 17" stroke="#ADC4DA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="33.25" y="33.25" width="32.5" height="32.5" rx="3.25" transform="rotate(180 33.25 33.25)" stroke="#ADC4DA" strokeWidth="1.5"/>
                  </svg>
                ) : (
                  <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg" style={{transform: 'rotate(180deg)'}}>
                    <path d="M17 9L25 17L17 25" stroke="#118BF4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M25 17H9" stroke="#118BF4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="0.75" y="0.75" width="32.5" height="32.5" rx="3.25" stroke="#118BF4" strokeWidth="1.5"/>
                  </svg>
                )}
              </button>
              <button
                id="home-properties-next-btn"
                onClick={goToNext}
                aria-label="goToNext"
                className={`custom-arrow next ${
                  currentSlide >= slideCount - (activeTab === "alerts"  || activeTab==="saved-properties"? settings1.slidesToShow:settings.slidesToShow)
                    ? "disabled"
                    : ""
                }`}
                disabled={currentSlide >= slideCount - (activeTab === "alerts"  || activeTab==="saved-properties"? settings1.slidesToShow:settings.slidesToShow)}
              >
                {currentSlide >= slideCount - (activeTab === "alerts"  || activeTab==="saved-properties"? settings1.slidesToShow:settings.slidesToShow) ? (
                  <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 9L25 17L17 25" stroke="#ADC4DA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M25 17H9" stroke="#ADC4DA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="0.75" y="0.75" width="32.5" height="32.5" rx="3.25" stroke="#ADC4DA" strokeWidth="1.5"/>
                  </svg>
                ) : (
                  <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 9L25 17L17 25" stroke="#118BF4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M25 17H9" stroke="#118BF4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="0.75" y="0.75" width="32.5" height="32.5" rx="3.25" stroke="#118BF4" strokeWidth="1.5"/>
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>
      {isLoading && <LoaderComponent />}
      {!isLoading && (
        <>
          {properties?.length > 0 ? (
            isMobile ? (
              <div className="mobile-vertical-scroll">
                {properties.slice(0, 5).map((item, index) => (
                  activeTab === 'alerts' ? (
                    <HomeAlertCard
                      key={item.alertId || index}
                      item={item}
                      cardIndex={index}
                    />
                  ) : activeTab === 'saved-properties' ? (
                    <SavedPropertyCard
                      key={item.propertyDetails?.propertyId || index}
                      item={item}
                      cardIndex={index}
                      properties={properties}
                      setProperties={setProperties}
                    />
                  ) : (
                    <PropertyCard
                      key={item.propertyId}
                      item={item}
                      cardIndex={index}
                      properties={properties}
                      setProperties={setProperties}
                      tabName={TABS.find(t => t.key === activeTab)?.label}
                    />
                  )
                ))}
              </div>
            ) : (
              <div className="col-lg-12">
                <div className="feature_property_slider gutter-x15">
                  <div className="ml10 mr20 ">
                    <Slider key={windowWidth} ref={sliderRef} {...(activeTab === 'alerts' || activeTab === 'saved-properties' ? customSettings1 : customSettings) }>
                      {properties.map((item, index) => (
                        activeTab === 'alerts' ? (
                          <HomeAlertCard
                            key={item.alertId || index}
                            item={item}
                            cardIndex={index}
                          />
                        ) : activeTab === 'saved-properties' ? (
                          <SavedPropertyCard
                            key={item.propertyDetails?.propertyId || index}
                            item={item}
                            cardIndex={index}
                            properties={properties}
                            setProperties={setProperties}
                          />
                        ) : (
                          <PropertyCard
                            key={item.propertyId}
                            item={item}
                            cardIndex={index}
                            properties={properties}
                            setProperties={setProperties}
                            tabName={TABS.find(t => t.key === activeTab)?.label}
                          />
                        )
                      ))}
                    </Slider>
                  </div>
                </div>
              </div>
            )
          ) : (
            <HomeEmptyCard activeTab={activeTab} />
          )}
        </>
      )}
    </section>
    </>
  );
};

export default memo(HomeProperties);